import { useEffect, useState } from 'react';

import { dispatchTaskSubmitted } from '@/components/DrawerTaskPanel/events';
import { LoginModal } from '@/components/UIComponents/CommonModal';
import UserStore from '@/store/User';
import { AIFunction, FC_Feature } from '@/utils/const';
import { getCookie } from '@/utils/cookie';
import { getGTMAppName, pushGTMEvent } from '@/utils/eventlog/gtmDataLayer';

import { Commodity } from '../const';
import { ContextData, GetUserLoginStateOptions } from '../context';
import { LimitConfig } from '../type';
import { getSinglePurchaseCommodities, transferLimitDataToMap } from '../utils';
import { getValidateUtils } from './useValidate';

export const useBenefit = () => {
  const [limitConfig, setLimitConfig] = useState<LimitConfig>({} as LimitConfig);
  const [singlePurchaseConfig, setSinglePurchaseConfig] = useState<Commodity>([]);
  const currentVip = UserStore((state) => state.currentVip);
  const functionLimitConfig = UserStore((state) => state.functionLimitConfig);
  const getUserBenefit = UserStore((state) => state.getUserBenefit);
  const getFunctionLimitConfig = UserStore((state) => state.getFunctionLimitConfig);
  const {
    getEntryValue,
    getIdentity,
    getRightConfig,
    getSinglePurchaseCommodity,
    getNextLevel,
    getPreviousLevel,
  } = getValidateUtils(limitConfig, singlePurchaseConfig, currentVip);

  /**
   * 检查用户登录状态，支持多种使用场景
   * @param options - 可选配置
   * @returns Promise<boolean> - true表示已登录或登录成功，false表示未登录或登录失败
   */
  const getUserLoginState = async (
    options?: GetUserLoginStateOptions,
  ): Promise<boolean> => {
    const user = getCookie('user');
    const token = user?.token;

    // 如果已登录
    if (token) {
      // 执行就绪回调（已登录也算就绪）
      options?.onReady?.();
      return true;
    }

    // 如果设置了 skipLoginModal，不弹窗，直接返回 false
    if (options?.skipLoginModal) {
      return false;
    }

    // 在弹窗前执行自定义逻辑
    if (options?.onBeforeLogin) {
      await options.onBeforeLogin();
    }

    // GTM auth_wall 埋点（skipGTMTracking 时跳过，用于已在外部上报的场景）
    if (!options?.skipGTMTracking) {
      if (options?.gtmAuthWall) {
        pushGTMEvent('login_popup', {
          trigger_type: 'auth_wall',
          app_name: options.gtmAuthWall.app_name,
          feature: options.gtmAuthWall.feature,
          usage_limit: options.gtmAuthWall.usage_limit,
        });
      } else {
        // 未显式传入时，从 sessionStorage FC_Feature 自动推断
        const currentFeature = sessionStorage.getItem(FC_Feature) as AIFunction | null;
        if (currentFeature) {
          const appName = getGTMAppName(currentFeature);
          pushGTMEvent('login_popup', {
            trigger_type: 'auth_wall',
            app_name: appName,
            feature: appName,
          });
        }
      }
    }

    try {
      const result = await LoginModal.show({
        onSuccess: async () => {
          try {
            // 1. 先更新权益
            await new Promise<void>((resolve) => {
              withUpdate(() => {
                resolve();
              }, true);
            });
            // 2. 权益更新完成后执行就绪回调
            options?.onReady?.();
            // 刷新任务列表
            dispatchTaskSubmitted();
          } catch (error) {
            console.error('Error in onReady callback:', error);
          }
        },
      });
      return result;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };
  /**
   * 动态获取&更新权益，使用最新权益进行校验
   */
  const withUpdate = async (
    thenDo?: (data: ContextData) => void,
    searchSeat?: boolean,
  ) => {
    const currentVip = await getUserBenefit(searchSeat);
    const functionLimitConfig = await getFunctionLimitConfig();
    const limitConfig = transferLimitDataToMap(functionLimitConfig);
    const singlePurchaseConfig = getSinglePurchaseCommodities(functionLimitConfig);
    const {
      getEntryValue,
      getIdentity,
      getRightConfig,
      getSinglePurchaseCommodity,
      getNextLevel,
      getPreviousLevel,
    } = getValidateUtils(limitConfig, singlePurchaseConfig, currentVip);
    thenDo &&
      thenDo({
        limitConfig,
        singlePurchaseConfig,
        currentVip,
        getUserLoginState,
        getIdentity,
        getRightConfig,
        getSinglePurchaseCommodity,
        getEntryValue,
        withUpdate,
        getNextLevel,
        getPreviousLevel,
        getIsLoggedIn,
      });
  };

  useEffect(() => {
    if (currentVip && functionLimitConfig) {
      try {
        const limitConfig = transferLimitDataToMap(functionLimitConfig);
        const singlePurchaseConfig = getSinglePurchaseCommodities(functionLimitConfig);
        setLimitConfig(limitConfig);
        setSinglePurchaseConfig(singlePurchaseConfig);
      } catch (error) {
        console.error(error);
      }
    }
  }, [currentVip, functionLimitConfig]);

  /**
   * 同步检查用户登录状态（不弹窗）
   * @returns boolean - true表示已登录，false表示未登录
   */
  const getIsLoggedIn = (): boolean => {
    const user = getCookie('user');
    return !!user?.token;
  };

  return {
    limitConfig,
    singlePurchaseConfig,
    currentVip,
    getIdentity,
    getRightConfig,
    getSinglePurchaseCommodity,
    getEntryValue,
    withUpdate,
    getNextLevel,
    getPreviousLevel,
    getUserLoginState,
    getIsLoggedIn,
  };
};
